import type {
  GameContext,
  GameInstance,
  GameModule,
  PreloadContext,
} from '@arcraz/arcadia-types';
import { CityHopInstance } from './instance.js';
import { PALETTE_HINT } from './palette.js';

/**
 * CITY HOP — fourth cartridge.
 *
 * Top-down grid-locked lane crosser. Frogger heritage with original
 * mechanics: 8-direction hops including diagonals, and diagonal sidewalk
 * tiles at intersections that let the player cut corners across two lanes
 * simultaneously — faster but riskier than the orthogonal route.
 *
 * MVP (sc-2874): alternating sidewalk + road bands, varied car traffic,
 * 3 lives, level-up on reaching top edge with difficulty ramp.
 */
const module_: GameModule = {
  id: 'city-hop',
  title: 'CITY HOP',
  manufacturer: 'ARCADIA AMUSEMENTS',
  year: 1984,
  genre: 'arena',
  resolution: { w: 480, h: 270 },
  pixelated: true,
  paletteHint: PALETTE_HINT,
  validatorVersion: 1,
  controls: [
    { keys: 'ARROWS / WASD', action: 'HOP (ORTHOGONAL)' },
    { keys: 'DIAGONAL KEYS', action: 'HOP (DIAGONAL)' },
    { keys: 'Z', action: 'RESTART (AT GAME OVER)' },
  ],

  async preload(ctx: PreloadContext): Promise<void> {
    // Hop: short rising blip on every move. Quiet enough not to fatigue
    // across rapid hops.
    ctx.registerSfx('hop', {
      wave: 'triangle',
      pitch: [{ t: 0, f: 660 }, { t: 40, f: 880 }],
      gain: [{ t: 0, g: 0 }, { t: 4, g: 0.16 }, { t: 40, g: 0.0 }],
    });
    // Crash: short noise burst with sub-bass thump (matches the racers'
    // crash character so the player's death sound is consistent across
    // the catalog).
    ctx.registerSfx('crash', {
      wave: 'noise',
      pitch: [{ t: 0, f: 80 }, { t: 200, f: 40 }],
      gain: [{ t: 0, g: 0 }, { t: 8, g: 0.55 }, { t: 200, g: 0.0 }],
    });
    // Life lost: descending two-note tone, distinct from the crash so the
    // player knows "you have N lives left" vs "you died."
    ctx.registerSfx('life-lost', {
      wave: 'square',
      pitch: [{ t: 0, f: 440 }, { t: 80, f: 330 }, { t: 160, f: 220 }],
      gain: [{ t: 0, g: 0 }, { t: 8, g: 0.25 }, { t: 160, g: 0.0 }],
    });
    // Level up: ascending triad fanfare when the player reaches the top.
    ctx.registerSfx('level-up', {
      wave: 'square',
      pitch: [
        { t: 0, f: 523 },
        { t: 100, f: 659 },
        { t: 200, f: 784 },
        { t: 300, f: 1046 },
      ],
      gain: [{ t: 0, g: 0 }, { t: 8, g: 0.3 }, { t: 300, g: 0.0 }],
    });
  },

  init(ctx: GameContext): GameInstance {
    return new CityHopInstance(ctx);
  },
};

export default module_;
